import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, FileText, Upload, Download, LogOut, Search,
  Loader2, ChevronRight, Shield, BarChart3, Clock,
  FolderOpen, Eye, Package, RefreshCw, X, CheckCircle2,
  AlertCircle, User, Mail, Phone, Calendar, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDocs, setClientDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getToken = () => localStorage.getItem('preciseo_admin_token');

  const authHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`
  });

  const authHeadersRef = useCallback(() => ({
    'Authorization': `Bearer ${getToken()}`
  }), []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, clientsRes] = await Promise.all([
        fetch(`${API}/admin/dashboard`, { headers: authHeadersRef() }),
        fetch(`${API}/admin/clients`, { headers: authHeadersRef() })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
    setLoading(false);
  }, [authHeadersRef]);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    // Load dashboard on mount
    (async () => {
      await loadDashboard();
    })();
  }, [navigate, loadDashboard]);

  const loadClientDocuments = async (clientId) => {
    try {
      const res = await fetch(`${API}/admin/clients/${clientId}/documents`, { headers: authHeaders() });
      if (res.ok) {
        const docs = await res.json();
        setClientDocs(docs);
      }
    } catch (err) {
      console.error('Failed to load client documents:', err);
    }
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setClientDocs([]);
    setSelectedFiles([]);
    setUploadResult(null);
    await loadClientDocuments(client.clientId);
  };

  const handleDownload = async (docId, filename) => {
    setDownloadingId(docId);
    try {
      const res = await fetch(`${API}/admin/documents/download/${docId}`, { headers: authHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloadingId(null);
  };

  const handleExportZip = async (clientId, clientName) => {
    setExportingId(clientId);
    try {
      const res = await fetch(`${API}/admin/clients/${clientId}/export`, { headers: authHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${clientName}_documents.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
    setExportingId(null);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setUploadResult(null);
  };

  const handleUploadForClient = async () => {
    if (!selectedClient || selectedFiles.length === 0) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    selectedFiles.forEach(file => formData.append('files', file));

    try {
      const res = await fetch(`${API}/admin/clients/${selectedClient.clientId}/upload`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData
      });

      const result = await res.json();
      setUploadResult(result);

      if (result.success) {
        setSelectedFiles([]);
        loadClientDocuments(selectedClient.clientId);
      }
    } catch (err) {
      setUploadResult({ success: false, message: 'Upload failed' });
    }
    setUploading(false);
  };

  const handleDeleteClient = async () => {
    if (!deletingClient) return;
    
    try {
      const res = await fetch(`${API}/admin/clients/${deletingClient.clientId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      
      if (res.ok) {
        // Refresh data
        loadDashboard();
        setSelectedClient(null);
        setClientDocs([]);
        setShowDeleteConfirm(false);
        setDeletingClient(null);
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to delete client');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete client');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('preciseo_admin_token');
    navigate('/admin/login');
  };

  const filteredClients = clients.filter(c =>
    c.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.clientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-secondary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-primary text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-secondary mr-3" />
            <div>
              <h1 className="text-xl font-bold">Preciseo Admin Portal</h1>
              <p className="text-xs text-white/60">Document Management System</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Clients</p>
                  <p className="text-3xl font-bold text-primary">{stats.totalClients}</p>
                </div>
                <Users className="w-10 h-10 text-secondary/20" />
              </div>
              <p className="text-xs text-green-600 mt-2">{stats.verifiedClients} verified</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Documents</p>
                  <p className="text-3xl font-bold text-primary">{stats.totalDocuments}</p>
                </div>
                <FileText className="w-10 h-10 text-secondary/20" />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Client Uploads</p>
                  <p className="text-3xl font-bold text-primary">{stats.clientUploads}</p>
                </div>
                <Upload className="w-10 h-10 text-blue-200" />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Preparer Uploads</p>
                  <p className="text-3xl font-bold text-primary">{stats.preparerUploads}</p>
                </div>
                <Download className="w-10 h-10 text-green-200" />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Client List */}
          <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-primary mb-4">Clients</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
            </div>

            <div className="max-h-[600px] overflow-y-auto">
              {filteredClients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => handleSelectClient(client)}
                  className={`w-full p-4 text-left border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                    selectedClient?.id === client.id ? 'bg-secondary/5 border-l-4 border-l-secondary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-primary">
                        {client.firstName} {client.lastName}
                      </p>
                      <p className="text-xs text-gray-400 font-mono">{client.clientId}</p>
                      <p className="text-xs text-gray-500">{client.email}</p>
                    </div>
                    <div className="text-right">
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                        {client.documentCount} docs
                      </span>
                      {client.isVerified ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-1 ml-auto" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-500 mt-1 ml-auto" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Client Details & Documents */}
          <div className="lg:col-span-2">
            {selectedClient ? (
              <div className="space-y-6">
                {/* Client Info */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-primary">
                        {selectedClient.firstName} {selectedClient.lastName}
                      </h2>
                      <p className="text-secondary font-mono">{selectedClient.clientId}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleExportZip(selectedClient.clientId, `${selectedClient.firstName}_${selectedClient.lastName}`)}
                        disabled={exportingId === selectedClient.clientId}
                        className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-light transition-colors disabled:opacity-50"
                      >
                        {exportingId === selectedClient.clientId ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Package className="w-4 h-4 mr-2" />
                        )}
                        Export ZIP
                      </button>
                      <button
                        onClick={() => {
                          setDeletingClient(selectedClient);
                          setShowDeleteConfirm(true);
                        }}
                        className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedClient.email}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedClient.phone}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedClient.timezone}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      {new Date(selectedClient.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Upload for Client */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-primary mb-4 flex items-center">
                    <Upload className="w-5 h-5 mr-2 text-secondary" />
                    Upload Documents for Client
                  </h3>

                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="admin-file-input"
                    />
                    <label htmlFor="admin-file-input" className="cursor-pointer">
                      <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">Click to select files</p>
                      <p className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG</p>
                    </label>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Selected: {selectedFiles.length} file(s)
                      </p>
                      <div className="space-y-1">
                        {selectedFiles.map((file, idx) => (
                          <div key={idx} className="text-xs text-gray-500 flex items-center">
                            <FileText className="w-3 h-3 mr-1" />
                            {file.name}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleUploadForClient}
                        disabled={uploading}
                        className="mt-4 w-full bg-secondary text-white py-3 rounded-xl font-bold hover:bg-secondary-hover transition-colors disabled:opacity-50"
                      >
                        {uploading ? (
                          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                        ) : (
                          'Upload to Client Portal'
                        )}
                      </button>
                    </div>
                  )}

                  {uploadResult && (
                    <div className={`mt-4 p-3 rounded-lg ${uploadResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {uploadResult.message}
                    </div>
                  )}
                </div>

                {/* Client Documents */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-primary flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-secondary" />
                      Client Documents ({clientDocs.length})
                    </h3>
                    <button
                      onClick={() => loadClientDocuments(selectedClient.clientId)}
                      className="text-secondary hover:text-secondary-hover"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </button>
                  </div>

                  {clientDocs.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No documents yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {clientDocs.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center">
                            <FileText className={`w-8 h-8 mr-3 ${doc.source === 'client' ? 'text-blue-500' : 'text-green-500'}`} />
                            <div>
                              <p className="font-medium text-primary text-sm">{doc.originalFilename}</p>
                              <div className="flex items-center text-xs text-gray-400 space-x-2">
                                <span>{(doc.size / 1024).toFixed(1)} KB</span>
                                <span>•</span>
                                <span className={`px-2 py-0.5 rounded-full ${doc.source === 'client' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                                  {doc.source === 'client' ? 'From Client' : 'From Preparer'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownload(doc.id, doc.originalFilename)}
                            disabled={downloadingId === doc.id}
                            className="p-2 text-secondary hover:bg-secondary/10 rounded-lg transition-colors"
                          >
                            {downloadingId === doc.id ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Download className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
                <Users className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-400">Select a Client</h3>
                <p className="text-gray-400 mt-2">Choose a client from the list to view their documents</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        {stats && stats.recentActivity && stats.recentActivity.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-primary mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-secondary" />
              Recent Activity
            </h3>
            <div className="space-y-3">
              {stats.recentActivity.map((activity, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-3 ${activity.source === 'client' ? 'bg-blue-500' : 'bg-green-500'}`} />
                    <div>
                      <p className="text-sm font-medium text-primary">{activity.clientName}</p>
                      <p className="text-xs text-gray-400">{activity.filename}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(activity.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Delete Client</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <strong>{deletingClient.firstName} {deletingClient.lastName}</strong>? 
                This will also delete all their documents. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletingClient(null);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteClient}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
